import React, { useEffect, useMemo, useState } from 'react'
import { Button, Message, Table } from 'semantic-ui-react'
import styled from 'styled-components'
import { importContacts, previewContactImport } from 'utility/services/crm'
import { toApiError } from 'utility/crm/api-error'
import { parseCsv } from 'utility/crm/csv-parse'
import { IMPORT_FIELDS, autoMapColumns, buildImportRows, hasNameColumn, importSizeError } from 'utility/crm/import-mapping'
import { useAuth } from 'context/user'
import { ALL_CRM_SCOPES, invalidateCrm, useCrmLookups, useCrmToast } from 'hooks/crm'
import { CrmModal } from './CrmOverlay'
import { Card, Muted, Pill, Row } from './ui'

const MAX_FILE_BYTES = 5 * 1024 * 1024
const PREVIEW_ROWS_SHOWN = 200
const STEPS = ['Choose file', 'Match columns', 'Preview', 'Done']
const label = { display: 'block', fontWeight: 600, margin: '10px 0 4px' }
const field = { width: '100%', padding: 8 }

const Stats = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 12px;
    @media (max-width: 560px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .n {
        font-size: 1.5rem;
        font-weight: 700;
    }
`

const ACTION_TONE = { new: 'green', update: 'blue', skip: 'gray' }

const stepTone = (index, current) => {
    if (index === current) return 'blue'
    return index < current ? 'green' : 'gray'
}

const readFileText = file =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () => reject(new Error('Could not read the file'))
        reader.readAsText(file)
    })

const ResultStats = ({ result }) => (
    <Stats>
        {[
            ['Will be added', result.Added],
            ['Will be updated', result.Updated],
            ['Skipped', result.Skipped],
            ['New companies', result.NewAccounts],
        ].map(([text, value]) => (
            <Card key={text}>
                <div className="n">{value}</div>
                <Muted>{text}</Muted>
            </Card>
        ))}
    </Stats>
)

/**
 * Contacts CSV import: choose a file, match columns (auto-detected by header), preview what will happen
 * (nothing is saved), then import. Duplicates are matched by email; max 2,000 rows.
 * @param {{ open: boolean, onClose: () => void }} props
 */
export const ImportWizard = ({ open, onClose }) => {
    const { user } = useAuth()
    const { lookups } = useCrmLookups()
    const { successToast } = useCrmToast()
    const [step, setStep] = useState(0)
    const [fileName, setFileName] = useState('')
    const [parsed, setParsed] = useState(null) // { headers, dataRows }
    const [fileError, setFileError] = useState(null)
    const [mapping, setMapping] = useState(autoMapColumns([]))
    const [owner, setOwner] = useState('')
    const [type, setType] = useState('Prospect')
    const [preview, setPreview] = useState(null)
    const [result, setResult] = useState(null)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState(null)

    useEffect(() => {
        if (!open) return
        setStep(0)
        setFileName('')
        setParsed(null)
        setFileError(null)
        setMapping(autoMapColumns([]))
        setPreview(null)
        setResult(null)
        setError(null)
        setType('Prospect')
        const me = (lookups?.Users || []).find(u => u.UserId === user?.unique_id)
        setOwner(me ? me.UserId : '')
    }, [open]) // eslint-disable-line

    const rows = useMemo(() => (parsed ? buildImportRows(parsed.dataRows, mapping) : []), [parsed, mapping])
    const sizeError = parsed ? importSizeError(rows.length) : null
    const nameMapped = hasNameColumn(mapping)

    const chooseFile = async event => {
        const file = event.target.files?.[0]
        setFileError(null)
        setParsed(null)
        if (!file) return
        if (file.size > MAX_FILE_BYTES) {
            setFileError('This file is larger than 5 MB. Split it into smaller files.')
            return
        }
        setFileName(file.name)
        try {
            const text = await readFileText(file)
            const { rows: all, unterminatedQuote } = parseCsv(text)
            if (all.length < 2) {
                setFileError('The file needs a header row and at least one data row.')
                return
            }
            if (unterminatedQuote) {
                setFileError('A quoted value in this file is never closed, so some rows may be wrong. Fix the quotes and choose the file again.')
                return
            }
            const [headers, ...dataRows] = all
            setParsed({ headers, dataRows })
            setMapping(autoMapColumns(headers))
        } catch (err) {
            setFileError(err.message || 'Could not read this file.')
        }
    }

    const body = () => ({ Rows: rows, OwnerUserId: owner === '' ? undefined : Number(owner), ContactType: type || undefined })

    const runPreview = async () => {
        setBusy(true)
        setError(null)
        try {
            setPreview(await previewContactImport(body()))
            setStep(2)
        } catch (err) {
            setError(toApiError(err).message)
        } finally {
            setBusy(false)
        }
    }

    const runImport = async () => {
        setBusy(true)
        setError(null)
        try {
            const done = await importContacts(body())
            setResult(done)
            setStep(3)
            successToast(`Import finished: ${done.Added} added, ${done.Updated} updated`)
            invalidateCrm(...ALL_CRM_SCOPES)
        } catch (err) {
            setError(toApiError(err).message)
        } finally {
            setBusy(false)
        }
    }

    const setCol = (key, value) => setMapping(m => ({ ...m, [key]: Number(value) }))
    const columnOptions = (
        <>
            <option value={-1}>Not in this file</option>
            {(parsed?.headers || []).map((h, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <option key={i} value={i}>
                    {h.trim() || `Column ${i + 1}`}
                </option>
            ))}
        </>
    )

    let footer
    if (step === 0) {
        footer = (
            <>
                <Button type="button" onClick={onClose}>
                    Cancel
                </Button>
                <Button type="button" primary disabled={!parsed} onClick={() => setStep(1)}>
                    Next: match columns
                </Button>
            </>
        )
    } else if (step === 1) {
        footer = (
            <>
                <Button type="button" onClick={() => setStep(0)} disabled={busy}>
                    Back
                </Button>
                <Button type="button" primary onClick={runPreview} loading={busy} disabled={busy || !nameMapped || Boolean(sizeError)}>
                    Preview import
                </Button>
            </>
        )
    } else if (step === 2) {
        footer = (
            <>
                <Button type="button" onClick={() => setStep(1)} disabled={busy}>
                    Back
                </Button>
                <Button type="button" primary onClick={runImport} loading={busy} disabled={busy || !preview || preview.Added + preview.Updated === 0}>
                    Import {preview ? preview.Added + preview.Updated : 0} contacts
                </Button>
            </>
        )
    } else {
        footer = (
            <Button type="button" primary onClick={onClose}>
                Done
            </Button>
        )
    }

    return (
        <CrmModal open={open} onClose={busy ? () => {} : onClose} title="Import contacts" width={780} footer={footer}>
            <Row $gap={6} $wrap aria-label={`Step ${step + 1} of ${STEPS.length}`} style={{ marginBottom: 12 }}>
                {STEPS.map((text, index) => (
                    <Pill key={text} $tone={stepTone(index, step)} aria-current={index === step ? 'step' : undefined}>
                        {index + 1} {text}
                    </Pill>
                ))}
            </Row>

            {error ? (
                <Message negative role="alert">
                    {error}
                </Message>
            ) : null}

            {step === 0 ? (
                <div>
                    <p style={{ marginTop: 0 }}>
                        Upload a CSV with one contact per row and a header row. Columns such as Name, Email, Company, Title, Phone and Tags are matched automatically. Contacts with an email that already exists are updated instead of duplicated.
                    </p>
                    <label htmlFor="import-file" style={label}>
                        CSV file (up to 2,000 rows)
                    </label>
                    <input id="import-file" type="file" accept=".csv,text/csv,text/plain" onChange={chooseFile} style={field} />
                    {fileError ? (
                        <Message negative role="alert" style={{ marginTop: 10 }}>
                            {fileError}
                        </Message>
                    ) : null}
                    {parsed ? (
                        <Message positive role="status" style={{ marginTop: 10 }}>
                            <strong>{fileName}</strong>: {parsed.dataRows.length.toLocaleString('en-US')} data row{parsed.dataRows.length === 1 ? '' : 's'}, {parsed.headers.length} column{parsed.headers.length === 1 ? '' : 's'}.
                        </Message>
                    ) : null}
                </div>
            ) : null}

            {step === 1 && parsed ? (
                <div>
                    <p style={{ marginTop: 0 }}>Check which column of your file goes into each field. We matched what we could by header name.</p>
                    {IMPORT_FIELDS.map(f => (
                        <Row key={f.key} $gap={12} style={{ marginBottom: 8 }}>
                            <label htmlFor={`map-${f.key}`} style={{ width: 130, fontWeight: 600 }}>
                                {f.label}
                                {f.required ? ' *' : ''}
                            </label>
                            <select id={`map-${f.key}`} value={mapping[f.key]} onChange={e => setCol(f.key, e.target.value)} style={{ flex: 1, padding: 8 }}>
                                {columnOptions}
                            </select>
                        </Row>
                    ))}
                    {mapping.Name < 0 ? (
                        <Card style={{ margin: '8px 0' }}>
                            <Muted>No Name column? Combine a first and last name column instead:</Muted>
                            <Row $gap={12} $wrap style={{ marginTop: 6 }}>
                                <select aria-label="First name column" value={mapping.FirstName} onChange={e => setCol('FirstName', e.target.value)} style={{ flex: 1, padding: 8 }}>
                                    {columnOptions}
                                </select>
                                <select aria-label="Last name column" value={mapping.LastName} onChange={e => setCol('LastName', e.target.value)} style={{ flex: 1, padding: 8 }}>
                                    {columnOptions}
                                </select>
                            </Row>
                        </Card>
                    ) : null}
                    {!nameMapped ? (
                        <Message warning role="alert">
                            Choose the column that holds each contact&apos;s name.
                        </Message>
                    ) : null}

                    <Row $gap={12} $wrap style={{ alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, minWidth: 180 }}>
                            <label htmlFor="import-owner" style={label}>
                                Owner for new contacts
                            </label>
                            <select id="import-owner" value={owner} onChange={e => setOwner(e.target.value)} style={field}>
                                <option value="">Unassigned</option>
                                {(lookups?.Users || []).map(u => (
                                    <option key={u.UserId} value={u.UserId}>
                                        {u.FullName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={{ flex: 1, minWidth: 180 }}>
                            <label htmlFor="import-type" style={label}>
                                Type
                            </label>
                            <select id="import-type" value={type} onChange={e => setType(e.target.value)} style={field}>
                                {(lookups?.AccountTypes || ['Prospect', 'Customer', 'Inactive']).map(t => (
                                    <option key={t} value={t}>
                                        {t}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </Row>

                    {sizeError ? (
                        <Message negative role="alert" style={{ marginTop: 12 }}>
                            {sizeError}
                        </Message>
                    ) : (
                        <Muted style={{ display: 'block', marginTop: 12 }}>{rows.length.toLocaleString('en-US')} contacts ready to preview.</Muted>
                    )}
                </div>
            ) : null}

            {step === 2 && preview ? (
                <div>
                    <p style={{ marginTop: 0 }}>This is what will happen. Nothing has been saved yet.</p>
                    <ResultStats result={preview} />
                    <div style={{ maxHeight: 320, overflow: 'auto' }}>
                        <Table compact="very" aria-label="Import preview">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Row</Table.HeaderCell>
                                    <Table.HeaderCell>Name</Table.HeaderCell>
                                    <Table.HeaderCell>Email</Table.HeaderCell>
                                    <Table.HeaderCell>Company</Table.HeaderCell>
                                    <Table.HeaderCell>Result</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {(preview.Rows || []).slice(0, PREVIEW_ROWS_SHOWN).map(r => (
                                    <Table.Row key={r.RowNumber}>
                                        <Table.Cell>{r.RowNumber}</Table.Cell>
                                        <Table.Cell>{r.Row?.Name}</Table.Cell>
                                        <Table.Cell>{r.Row?.Email}</Table.Cell>
                                        <Table.Cell>{r.Row?.Company}</Table.Cell>
                                        <Table.Cell>
                                            <Pill $tone={ACTION_TONE[r.Action] || 'gray'}>{r.Action}</Pill>
                                            {r.Reason ? <Muted> {r.Reason}</Muted> : null}
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </div>
                    {(preview.Rows || []).length > PREVIEW_ROWS_SHOWN ? <Muted>Showing the first {PREVIEW_ROWS_SHOWN} of {preview.Rows.length} rows.</Muted> : null}
                </div>
            ) : null}

            {step === 3 && result ? (
                <div role="status">
                    <Message positive>
                        <Message.Header>Import complete</Message.Header>
                        Each new contact also got an “Intro call” follow-up.
                    </Message>
                    <Stats>
                        {[
                            ['Added', result.Added],
                            ['Updated', result.Updated],
                            ['Skipped', result.Skipped],
                            ['New companies', result.NewAccounts],
                        ].map(([text, value]) => (
                            <Card key={text}>
                                <div className="n">{value}</div>
                                <Muted>{text}</Muted>
                            </Card>
                        ))}
                    </Stats>
                    {(result.Rows || []).filter(r => r.Action === 'skip').length > 0 ? (
                        <>
                            <strong>Skipped rows</strong>
                            <ul>
                                {(result.Rows || [])
                                    .filter(r => r.Action === 'skip')
                                    .slice(0, 20)
                                    .map(r => (
                                        <li key={r.RowNumber}>
                                            Row {r.RowNumber}: {r.Reason || 'skipped'}
                                        </li>
                                    ))}
                            </ul>
                        </>
                    ) : null}
                </div>
            ) : null}
        </CrmModal>
    )
}
